import { apiClient } from './client'

interface ErrorTrackingRequest {
  service_name: string
  environment: string
  timestamp: string
  attributes: Record<string, unknown>
  error: string
}

interface ErrorTrackingResponse {
  id: string
  timestamp: string
  status: string
}

/**
 * Track a swap-related error with additional context
 * This is a specific implementation to serve the needs of the swap error tracking
 * It can be expanded in the future for more generic usage
 *
 * @param error - The error to track
 * @param context - The context of the error
 * @returns The response from the error tracking service
 */
export const trackSwapError = async (
  error?: Error,
  context?: {
    route?: {
      ID: string
      sourceChain: { chain_name: string; chain_type: string | number }
      destinationChain: { chain_name: string; chain_type: string | number }
    }
    page?: string
    timeToSwap?: number
    stepNumber?: number
    errorType?: 'step_failed' | 'swap_failed' | 'retryable_error'
  },
): Promise<ErrorTrackingResponse> => {
  // Map Vercel environment values to what the error tracker expects
  // Got these values from the atlas ui implementation
  // https://github.com/polaris-portal/polaris/blob/5c51f1b77854101baf6c88dcd81ec683fad185e0/ui/apps/atlas/src/pages/error-tracker/list.tsx#L307-L309
  const environmentMapping: Record<string, string> = {
    development: 'local',
    preview: 'stage',
    production: 'prod',
  }

  const rawEnvironment = process.env.NEXT_PUBLIC_VERCEL_ENV || 'development'
  const environment = environmentMapping[rawEnvironment] || 'local'

  return apiClient
    .post('errortracker/track', {
      prefixUrl: 'https://api-stage.polaris.app/',
      json: {
        service_name: 'web',
        environment,
        timestamp: new Date().toISOString(),
        attributes: {
          error: error?.message,
          stack: error?.stack,
          userAgent: navigator.userAgent,
          url: window.location.href,
          category: 'swap',
          errorType: context?.errorType || 'unknown',
          routeId: context?.route?.ID,
          sourceChain: context?.route?.sourceChain?.chain_name,
          sourceChainType: String(context?.route?.sourceChain?.chain_type),
          destinationChain: context?.route?.destinationChain?.chain_name,
          destinationChainType: String(
            context?.route?.destinationChain?.chain_type,
          ),
          page: context?.page,
          timeToSwap: context?.timeToSwap,
          stepNumber: context?.stepNumber,
        },
        error: error?.message ?? 'unknown',
      } satisfies ErrorTrackingRequest,
    })
    .json<ErrorTrackingResponse>()
}

/**
 * Wrapper for trackSwapError to safely track errors without throwing
 */
export const safeTrackSwapError = async (
  error?: Error,
  context?: Parameters<typeof trackSwapError>[1],
): Promise<void> => {
  try {
    await trackSwapError(error, context)
  } catch (trackingError) {
    console.error('Failed to track swap error:', trackingError)
  }
}
